/**
 * Tempo detection (W2-08 timing; ARCHITECTURE.md §3.2 `core/audio-dsp/tempo.ts`, DESIGN.md §5.2 "Detect").
 *
 * Input: the spectral-flux onset envelope (onset.ts). Pipeline:
 *  1. **Tempogram**: the autocorrelation of the mean-free envelope (through the FFT, unbiased: each lag divided by
 *     the number of overlapping frames, so long lags are not penalised) is read at the beat period of every tempo
 *     from `minBpm` to `maxBpm` (0.5 BPM steps). **Harmonic weighting** adds the autocorrelation at 2, 3 and 4
 *     periods with weights 1/2, 1/3, 1/4, so a tempo whose multiples also repeat beats one that only matches once,
 *     and a gentle log-normal prior (centre 140 BPM, one octave wide) breaks the tie between a tempo and its half,
 *     whose harmonics are the same beats.
 *  2. **Seeds**: the best local maximum, its metrical relatives (x2, /2, x3 and /3, when inside the range: tempo
 *     detection is famously unsure about octaves, and a fast tempo can peak at its third, e.g. 68 for 204 BPM) and
 *     further local maxima.
 *  3. **Refinement** of every seed: a coarse-to-fine search of (BPM, phase) maximising the envelope summed on
 *     the beat grid (0.1 then 0.01 BPM steps), then a weighted least-squares fit of the onset times matched to the
 *     grid (onset n at time phase + n * period), optionally with exact onset times from the PCM (`refineOnset`).
 *     A value within its own error bar of a whole BPM is rounded to it (charts are usually written in whole BPMs).
 *  4. **Strength** (0..1): the F-measure of the beat grid against the detected onsets: recall = share of onset
 *     strength within the tolerance of a grid beat, precision = share of grid beats with an onset. The true tempo
 *     of a click track scores about 1; its half about 0.67 (every other onset is off the grid); noise about 0.2.
 *  5. **Order** (best first): the tempogram's choice, except that the prior's preference for about 140 BPM does not
 *     halve or third a fast song: when the double (or triple) of the lead is also a candidate, fits the onsets
 *     better, and the onsets halfway (a third and two thirds of the way) between the lead's beats are about as
 *     strong as those on them (`offbeatRatio` >= EVEN_OFFBEAT_RATIO: those are beats too, not hi-hats or eighth
 *     notes), the multiple leads instead (repeated, so a quarter of the tempo climbs twice). The other candidates
 *     follow by strength, and only then the list is cut to `maxCandidates`: a strong relative found from any seed
 *     is never dropped for a weaker one refined earlier.
 */

import { createFft, nextPow2 } from './fft';
import type { OnsetEnvelope, TempoCandidate } from './types';

/** Detection range of DESIGN.md §5.2 ("autocorrelation tempogram over 60-240 BPM"). */
export const MIN_BPM = 60;
export const MAX_BPM = 240;
/** Default number of candidates ("up to 3 candidates with strength labels"). */
export const MAX_CANDIDATES = 3;

/** Harmonic weights of the tempogram (period multiples 1..4). */
const HARMONIC_WEIGHTS: readonly number[] = [1, 1 / 2, 1 / 3, 1 / 4];
/** Log-normal tempo prior: centre (BPM) and width (octaves). */
const PRIOR_CENTER_BPM = 140;
const PRIOR_WIDTH_OCTAVES = 1.1;
/** Scan step of the tempogram (BPM). */
const SCAN_STEP = 0.5;
/** Candidates closer than this ratio are the same tempo. */
const SAME_TEMPO_RATIO = 0.02;
/** `offbeatRatio` from which the onsets between the beats count as beats of their own (step 5). */
export const EVEN_OFFBEAT_RATIO = 0.6;

/** A detected onset: time (s, music time) and strength (envelope value, 0..1). */
export interface OnsetPeak {
  readonly t: number;
  readonly v: number;
}

export interface PeakOptions {
  /** Absolute floor (default 0.04). */
  readonly threshold?: number;
  /** A peak must reach this fraction of the largest value within +-1 s (default 0.1). */
  readonly relative?: number;
  /** Peaks closer than this keep only the larger (default 0.05 s). */
  readonly minGapSec?: number;
}

/**
 * Local maxima of the envelope (parabolic interpolation of time and value), above an absolute floor and a fraction
 * of the local maximum, at least `minGapSec` apart.
 */
export function onsetPeaks(e: OnsetEnvelope, opts: PeakOptions = {}): OnsetPeak[] {
  const env = e.env;
  const n = env.length;
  const floor = opts.threshold ?? 0.04;
  const relative = opts.relative ?? 0.1;
  const minGap = opts.minGapSec ?? 0.05;
  const radius = Math.max(1, Math.round(1 / e.hopSec));
  const localMax = slidingMax(env, radius);
  const out: OnsetPeak[] = [];
  for (let i = 1; i + 1 < n; i++) {
    const b = env[i] ?? 0;
    const a = env[i - 1] ?? 0;
    const c = env[i + 1] ?? 0;
    if (!(b > a && b >= c)) continue;
    if (b < floor || b < relative * (localMax[i] ?? 0)) continue;
    const denom = a - 2 * b + c;
    const delta = denom < 0 ? Math.max(-0.5, Math.min(0.5, (0.5 * (a - c)) / denom)) : 0;
    const peak: OnsetPeak = { t: (i + delta) * e.hopSec, v: b - 0.25 * (a - c) * delta };
    const last = out[out.length - 1];
    if (last !== undefined && peak.t - last.t < minGap) {
      if (peak.v > last.v) out[out.length - 1] = peak;
      continue;
    }
    out.push(peak);
  }
  return out;
}

/** Maximum over [i - r, i + r] for every i (monotonic deque, O(n)). */
export function slidingMax(x: ArrayLike<number>, r: number): Float64Array {
  const n = x.length;
  const out = new Float64Array(n);
  const dq = new Int32Array(n + 1);
  let head = 0;
  let tail = 0;
  let next = 0;
  for (let i = 0; i < n; i++) {
    const hi = Math.min(n - 1, i + r);
    while (next <= hi) {
      const v = x[next] ?? 0;
      while (tail > head && (x[dq[tail - 1] ?? 0] ?? 0) <= v) tail--;
      dq[tail++] = next;
      next++;
    }
    while ((dq[head] ?? 0) < i - r) head++;
    out[i] = x[dq[head] ?? 0] ?? 0;
  }
  return out;
}

/**
 * Normalised autocorrelation of the mean-free signal for lags 0..maxLag (FFT; unbiased: lag k divided by n - k,
 * then by lag 0). Lags at or beyond the signal length are 0.
 */
export function autocorrelation(x: ArrayLike<number>, maxLag: number): Float64Array {
  const n = x.length;
  const lags = Math.max(0, Math.floor(maxLag));
  const out = new Float64Array(lags + 1);
  if (n === 0) return out;
  let mean = 0;
  for (let i = 0; i < n; i++) mean += x[i] ?? 0;
  mean /= n;
  const size = nextPow2(2 * n);
  const fft = createFft(size);
  const re = new Float64Array(size);
  const im = new Float64Array(size);
  for (let i = 0; i < n; i++) re[i] = (x[i] ?? 0) - mean;
  fft.forward(re, im);
  for (let k = 0; k < size; k++) {
    const a = re[k] ?? 0;
    const b = im[k] ?? 0;
    re[k] = a * a + b * b;
    im[k] = 0;
  }
  fft.inverse(re, im);
  const zero = (re[0] ?? 0) / n;
  if (!(zero > 0)) return out;
  for (let k = 0; k <= lags && k < n; k++) out[k] = (re[k] ?? 0) / (n - k) / zero;
  return out;
}

/** Linear interpolation of `a` at a fractional index (0 outside). */
function interp(a: ArrayLike<number>, x: number): number {
  if (!(x >= 0)) return 0;
  const i = Math.floor(x);
  if (i >= a.length) return 0;
  const f = x - i;
  const v0 = a[i] ?? 0;
  const v1 = i + 1 < a.length ? (a[i + 1] ?? 0) : 0;
  return v0 + (v1 - v0) * f;
}

/** Log-normal tempo prior (1 at PRIOR_CENTER_BPM). */
export function tempoPrior(bpm: number): number {
  const o = Math.log2(bpm / PRIOR_CENTER_BPM) / PRIOR_WIDTH_OCTAVES;
  return Math.exp(-0.5 * o * o);
}

export interface Tempogram {
  readonly bpms: Float64Array;
  /** Harmonic-weighted autocorrelation per BPM (no prior). */
  readonly raw: Float64Array;
  /** `raw` times the tempo prior. */
  readonly score: Float64Array;
}

/** Harmonic-weighted autocorrelation at the beat period of `bpm` (positive part only). */
export function harmonicScore(acf: ArrayLike<number>, hopSec: number, bpm: number): number {
  const lag = 60 / (bpm * hopSec);
  let s = 0;
  for (let h = 0; h < HARMONIC_WEIGHTS.length; h++) {
    s += (HARMONIC_WEIGHTS[h] ?? 0) * Math.max(0, interp(acf, lag * (h + 1)));
  }
  return s;
}

/** The tempogram of an envelope over [minBpm, maxBpm] (SCAN_STEP steps). */
export function tempogram(e: OnsetEnvelope, minBpm = MIN_BPM, maxBpm = MAX_BPM, acf?: Float64Array): Tempogram {
  const lo = Math.max(1, Math.min(minBpm, maxBpm));
  const hi = Math.max(lo, maxBpm);
  const count = Math.floor((hi - lo) / SCAN_STEP) + 1;
  const a = acf ?? autocorrelation(e.env, Math.ceil((60 / (lo * e.hopSec)) * HARMONIC_WEIGHTS.length) + 2);
  const bpms = new Float64Array(count);
  const raw = new Float64Array(count);
  const score = new Float64Array(count);
  for (let i = 0; i < count; i++) {
    const bpm = lo + i * SCAN_STEP;
    bpms[i] = bpm;
    raw[i] = harmonicScore(a, e.hopSec, bpm);
    score[i] = (raw[i] ?? 0) * tempoPrior(bpm);
  }
  return { bpms, raw, score };
}

/** Beat-grid fit of a tempo: precision, recall and their F-measure (see the module header). */
export interface GridFit {
  readonly precision: number;
  readonly recall: number;
  readonly f: number;
}

/** Matching tolerance (s) of an onset to a beat of `periodSec`. */
export function beatTolerance(periodSec: number): number {
  return Math.max(0.02, Math.min(0.05, 0.1 * periodSec));
}

/** Index of the first element of the sorted `peaks` with t >= x. */
function lowerBound(peaks: readonly OnsetPeak[], x: number): number {
  let lo = 0;
  let hi = peaks.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if ((peaks[mid]?.t ?? 0) < x) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

/** The peak nearest to `t` within `tol`, or null. */
export function nearestPeak(peaks: readonly OnsetPeak[], t: number, tol: number): OnsetPeak | null {
  const i = lowerBound(peaks, t - tol);
  let best: OnsetPeak | null = null;
  for (let j = i; j < peaks.length; j++) {
    const p = peaks[j];
    if (p === undefined || p.t > t + tol) break;
    if (best === null || Math.abs(p.t - t) < Math.abs(best.t - t)) best = p;
  }
  return best;
}

/** Grid fit of beats at phase + k * period over the span of the peaks. */
export function gridFit(peaks: readonly OnsetPeak[], periodSec: number, phaseSec: number): GridFit {
  const first = peaks[0];
  const last = peaks[peaks.length - 1];
  if (first === undefined || last === undefined || !(periodSec > 0)) return { precision: 0, recall: 0, f: 0 };
  const tol = beatTolerance(periodSec);
  let total = 0;
  let hit = 0;
  for (const p of peaks) {
    total += p.v;
    const k = Math.round((p.t - phaseSec) / periodSec);
    if (Math.abs(p.t - (phaseSec + k * periodSec)) <= tol) hit += p.v;
  }
  const k0 = Math.ceil((first.t - tol - phaseSec) / periodSec);
  const k1 = Math.floor((last.t + tol - phaseSec) / periodSec);
  let beats = 0;
  let matched = 0;
  for (let k = k0; k <= k1; k++) {
    beats++;
    if (nearestPeak(peaks, phaseSec + k * periodSec, tol) !== null) matched++;
  }
  const recall = total > 0 ? hit / total : 0;
  const precision = beats > 0 ? matched / beats : 0;
  const f = precision + recall > 0 ? (2 * precision * recall) / (precision + recall) : 0;
  return { precision, recall, f };
}

/**
 * Mean of the envelope on the grid anchor + k * period (every integer k with a beat in [from, to]). Anchoring the
 * grid inside the analysed range (not at time 0) keeps the best anchor still while the period is searched.
 */
function gridScore(
  env: ArrayLike<number>,
  hopSec: number,
  periodSec: number,
  anchorSec: number,
  from: number,
  to: number,
): number {
  const k0 = Math.ceil((from - anchorSec) / periodSec);
  const k1 = Math.floor((to - anchorSec) / periodSec);
  if (k1 < k0) return 0;
  let s = 0;
  for (let k = k0; k <= k1; k++) s += interp(env, (anchorSec + k * periodSec) / hopSec);
  return s / (k1 - k0 + 1);
}

/** Best grid anchor in [center - radius, center + radius] (steps of `step` seconds) and its score. */
function bestAnchor(
  env: ArrayLike<number>,
  hopSec: number,
  periodSec: number,
  from: number,
  to: number,
  step: number,
  center: number,
  radius: number,
): { readonly anchor: number; readonly score: number } {
  let anchor = center;
  let score = -Infinity;
  for (let a = center - radius; a < center + radius + step * 0.5; a += step) {
    const s = gridScore(env, hopSec, periodSec, a, from, to);
    if (s > score) {
      score = s;
      anchor = a;
    }
  }
  return { anchor, score };
}

export interface RefinedTempo {
  readonly bpm: number;
  /** Music time (s) of a beat of the fitted grid (in [0, period)). */
  readonly phaseSec: number;
  readonly fit: GridFit;
}

export interface RefineOptions {
  /** Exact onset time near `approxSec` (within `windowSec`), e.g. from the PCM's fine novelty; null when none. */
  readonly refineOnset?: (approxSec: number, windowSec: number) => number | null;
  /** Largest relative change the search considers (default 0.025 = 2.5 %). */
  readonly searchRatio?: number;
}

interface Regression {
  readonly period: number;
  readonly phase: number;
  /** Standard error of the period (s). */
  readonly periodSe: number;
  readonly count: number;
}

/** Weighted least squares t = a + b k over onsets matched to the grid (null with fewer than 4 matches). */
function regress(
  peaks: readonly OnsetPeak[],
  periodSec: number,
  phaseSec: number,
  tol: number,
  refine: RefineOptions['refineOnset'],
): Regression | null {
  const ks: number[] = [];
  const ts: number[] = [];
  const ws: number[] = [];
  for (const p of peaks) {
    const k = Math.round((p.t - phaseSec) / periodSec);
    const g = phaseSec + k * periodSec;
    if (Math.abs(p.t - g) > tol) continue;
    const exact = refine?.(p.t, tol) ?? null;
    ks.push(k);
    ts.push(exact ?? p.t);
    ws.push(p.v);
  }
  const n = ks.length;
  if (n < 4) return null;
  let sw = 0;
  let sk = 0;
  let st = 0;
  for (let i = 0; i < n; i++) {
    const w = ws[i] ?? 0;
    sw += w;
    sk += w * (ks[i] ?? 0);
    st += w * (ts[i] ?? 0);
  }
  const mk = sk / sw;
  const mt = st / sw;
  let skk = 0;
  let skt = 0;
  let ukk = 0;
  for (let i = 0; i < n; i++) {
    const w = ws[i] ?? 0;
    const dk = (ks[i] ?? 0) - mk;
    skk += w * dk * dk;
    skt += w * dk * ((ts[i] ?? 0) - mt);
    ukk += dk * dk;
  }
  if (!(skk > 0)) return null;
  const b = skt / skk;
  const a = mt - b * mk;
  let rss = 0;
  for (let i = 0; i < n; i++) {
    const r = (ts[i] ?? 0) - (a + b * (ks[i] ?? 0));
    rss += (ws[i] ?? 0) * r * r;
  }
  const sigma = Math.sqrt(rss / sw);
  return { period: b, phase: a, periodSe: sigma / Math.sqrt(Math.max(ukk, 1e-12)), count: n };
}

/**
 * Refines a tempo estimate (see the module header, step 3). `peaks` must be `onsetPeaks(e)`.
 * Returns the BPM (rounded to a whole BPM when within its error bar), a grid phase and the grid fit.
 */
export function refineTempo(
  e: OnsetEnvelope,
  peaks: readonly OnsetPeak[],
  bpm0: number,
  opts: RefineOptions = {},
): RefinedTempo {
  const env = e.env;
  const hop = e.hopSec;
  const first = peaks[0];
  const last = peaks[peaks.length - 1];
  const from = first === undefined ? 0 : Math.max(0, first.t - 0.05);
  const to = last === undefined ? env.length * hop : last.t + 0.05;
  const ratio = opts.searchRatio ?? 0.025;
  const mid = (from + to) / 2;
  // Coarse: 0.1 BPM steps, anchors in half hops over one period around the middle of the range.
  let best = { bpm: bpm0, anchor: mid, score: -Infinity };
  const lo = bpm0 * (1 - ratio);
  const hi = bpm0 * (1 + ratio);
  for (let b = lo; b <= hi + 1e-9; b += 0.1) {
    const period = 60 / b;
    const r = bestAnchor(env, hop, period, from, to, hop / 2, mid, period / 2);
    if (r.score > best.score) best = { bpm: b, anchor: r.anchor, score: r.score };
  }
  // Fine: 0.01 BPM steps around the coarse value, anchors in eighth hops near the coarse anchor.
  const coarse = best;
  for (let b = coarse.bpm - 0.12; b <= coarse.bpm + 0.12 + 1e-9; b += 0.01) {
    if (!(b > 0)) continue;
    const r = bestAnchor(env, hop, 60 / b, from, to, hop / 8, coarse.anchor, hop * 1.5);
    if (r.score > best.score) best = { bpm: b, anchor: r.anchor, score: r.score };
  }
  let period = 60 / best.bpm;
  let phase = best.anchor;
  let periodSe = Infinity;
  // Least squares over the matched onsets (two passes: the second with the improved grid).
  for (let pass = 0; pass < 2; pass++) {
    const r = regress(peaks, period, phase, beatTolerance(period), opts.refineOnset);
    if (r === null || !(r.period > 0) || Math.abs(r.period - 60 / best.bpm) / (60 / best.bpm) > 0.005) break;
    period = r.period;
    phase = r.phase;
    periodSe = r.periodSe;
  }
  let bpm = 60 / period;
  const whole = Math.round(bpm);
  const bpmSe = Number.isFinite(periodSe) ? (60 / (period * period)) * periodSe : Infinity;
  if (Math.abs(bpm - whole) <= Math.min(0.05, Math.max(0.01, 2 * bpmSe))) {
    // Keep the grid where the fit put it in the middle of the range: re-anchor there.
    const k = Math.round((mid - phase) / period);
    const anchor = phase + k * period;
    bpm = whole;
    period = 60 / whole;
    phase = anchor;
  }
  phase = ((phase % period) + period) % period;
  return { bpm, phaseSec: phase, fit: gridFit(peaks, period, phase) };
}

/** Local maxima of the tempogram score (indices), best first. */
function localMaxima(score: Float64Array): number[] {
  const out: number[] = [];
  for (let i = 0; i < score.length; i++) {
    const v = score[i] ?? 0;
    if (!(v > 0)) continue;
    const a = i > 0 ? (score[i - 1] ?? 0) : -Infinity;
    const c = i + 1 < score.length ? (score[i + 1] ?? 0) : -Infinity;
    if (v >= a && v > c) out.push(i);
  }
  return out.sort((x, y) => (score[y] ?? 0) - (score[x] ?? 0));
}

/** Parabolic refinement of a tempogram maximum (BPM). */
function peakBpm(t: Tempogram, i: number): number {
  const a = t.score[i - 1];
  const b = t.score[i] ?? 0;
  const c = t.score[i + 1];
  const x = t.bpms[i] ?? 0;
  if (a === undefined || c === undefined) return x;
  const denom = a - 2 * b + c;
  if (!(denom < 0)) return x;
  return x + Math.max(-0.5, Math.min(0.5, (0.5 * (a - c)) / denom)) * SCAN_STEP;
}

export interface TempoOptions extends RefineOptions {
  readonly minBpm?: number;
  readonly maxBpm?: number;
  readonly maxCandidates?: number;
  /** Progress 0..1. */
  readonly onProgress?: (p: number) => void;
}

/** A refined candidate with its grid. */
export interface DetailedCandidate extends TempoCandidate {
  readonly phaseSec: number;
  readonly fit: GridFit;
}

function sameTempo(a: number, b: number): boolean {
  return Math.abs(a - b) <= SAME_TEMPO_RATIO * Math.max(a, b);
}

/** Largest envelope value within `radius` seconds of `t` (0 outside the envelope). */
function envMaxNear(e: OnsetEnvelope, t: number, radius: number): number {
  const i0 = Math.max(0, Math.floor((t - radius) / e.hopSec));
  const i1 = Math.min(e.env.length - 1, Math.ceil((t + radius) / e.hopSec));
  let m = 0;
  for (let i = i0; i <= i1; i++) m = Math.max(m, e.env[i] ?? 0);
  return m;
}

/**
 * Onset strength between the beats of a grid over the onset strength on them (mean of the envelope's maxima near
 * each point, over the span of the envelope). With `divisions` 2 the point is halfway between two beats; with 3 the
 * points a third and two thirds of the way (their mean). About 1 when every such point is as strong as a beat (the
 * grid is a half or a third of the tempo of the music), small when the beats carry the onsets. 0 without onsets on
 * the grid.
 */
export function offbeatRatio(e: OnsetEnvelope, periodSec: number, phaseSec: number, divisions = 2): number {
  const d = Math.max(2, Math.floor(divisions));
  if (!(periodSec > 0) || e.env.length === 0) return 0;
  const duration = e.env.length * e.hopSec;
  const step = periodSec / d;
  const radius = Math.min(0.02, step / 4);
  let on = 0;
  let off = 0;
  const k0 = Math.ceil(-phaseSec / periodSec);
  for (let k = k0; ; k++) {
    const t = phaseSec + k * periodSec;
    if (t + (d - 1) * step > duration) break;
    on += envMaxNear(e, t, radius);
    let between = 0;
    for (let j = 1; j < d; j++) between += envMaxNear(e, t + j * step, radius);
    off += between / (d - 1);
  }
  return on > 0 ? off / on : 0;
}

/** Factors of the metrical relatives seeded next to the tempogram's best tempo (module header, step 2). */
const RELATIVE_FACTORS: readonly number[] = [0.5, 2, 1 / 3, 3];
/** Further tempogram maxima seeded besides the best one and its relatives. */
const EXTRA_SEEDS = 4;
/** Multiples a lead can climb to (module header, step 5). */
const LEAD_MULTIPLES: readonly number[] = [2, 3];

/** Candidates with their grids (see the module header). Empty for silence or too little rhythm. */
export function detectTempo(e: OnsetEnvelope, opts: TempoOptions = {}): DetailedCandidate[] {
  const minBpm = opts.minBpm ?? MIN_BPM;
  const maxBpm = opts.maxBpm ?? MAX_BPM;
  const max = Math.max(1, Math.floor(opts.maxCandidates ?? MAX_CANDIDATES));
  const peaks = onsetPeaks(e);
  opts.onProgress?.(0.1);
  if (peaks.length < 4 || !(maxBpm >= minBpm) || !(minBpm > 0)) {
    opts.onProgress?.(1);
    return [];
  }
  const t = tempogram(e, minBpm, maxBpm);
  opts.onProgress?.(0.3);
  const maxima = localMaxima(t.score);
  const top = maxima[0];
  if (top === undefined) {
    opts.onProgress?.(1);
    return [];
  }
  const seeds: number[] = [];
  const addSeed = (b: number): void => {
    if (b >= minBpm && b <= maxBpm && !seeds.some((s) => sameTempo(s, b))) seeds.push(b);
  };
  const bestBpm = peakBpm(t, top);
  addSeed(bestBpm);
  for (const factor of RELATIVE_FACTORS) addSeed(bestBpm * factor);
  let extra = 0;
  for (const i of maxima.slice(1)) {
    if (extra >= Math.max(EXTRA_SEEDS, max)) break;
    const before = seeds.length;
    addSeed(peakBpm(t, i));
    if (seeds.length > before) extra++;
  }
  // Every seed is refined before the list is cut: the order (and the cut) depends on the refined strengths.
  const refined: DetailedCandidate[] = [];
  for (const [i, seed] of seeds.entries()) {
    const r = refineTempo(e, peaks, seed, opts);
    opts.onProgress?.(0.3 + (0.7 * (i + 1)) / seeds.length);
    if (r.bpm < minBpm - 0.5 || r.bpm > maxBpm + 0.5) continue;
    const c: DetailedCandidate = {
      bpm: roundBpm(r.bpm),
      strength: clamp01(r.fit.f),
      phaseSec: r.phaseSec,
      fit: r.fit,
    };
    const same = refined.findIndex((x) => sameTempo(x.bpm, c.bpm));
    if (same < 0) refined.push(c);
    // Two seeds refined to the same tempo: keep the better fit, in the place of the first (the tempogram's order).
    else if (c.strength > (refined[same]?.strength ?? 0)) refined[same] = c;
  }
  opts.onProgress?.(1);
  return orderCandidates(e, refined).slice(0, max);
}

/**
 * Best first (module header, step 5): the tempogram's choice, replaced by its double or triple while that fits the
 * onsets better and the points between the lead's beats are beats too; the others by strength.
 */
function orderCandidates(e: OnsetEnvelope, list: readonly DetailedCandidate[]): DetailedCandidate[] {
  const first = list[0];
  if (first === undefined) return [];
  let lead = first;
  for (let climb = 0; climb < 4; climb++) {
    const from = lead;
    const up = LEAD_MULTIPLES.map((k) => ({ k, c: list.find((x) => sameTempo(x.bpm, from.bpm * k)) }))
      .filter(
        (x): x is { k: number; c: DetailedCandidate } =>
          x.c !== undefined &&
          x.c.strength > from.strength &&
          offbeatRatio(e, 60 / from.bpm, from.phaseSec, x.k) >= EVEN_OFFBEAT_RATIO,
      )
      .sort((a, b) => b.c.strength - a.c.strength)[0];
    if (up === undefined) break;
    lead = up.c;
  }
  const rest = list.filter((c) => c !== lead).sort((a, b) => b.strength - a.strength);
  return [lead, ...rest];
}

/** Tempo candidates for the worker protocol (`TempoCandidate`: bpm and strength 0..1), best first. */
export function tempoCandidates(e: OnsetEnvelope, opts: TempoOptions = {}): TempoCandidate[] {
  return detectTempo(e, opts).map((c) => ({ bpm: c.bpm, strength: c.strength }));
}

/** BPM rounded to 0.01 (what the BPM field shows). */
export function roundBpm(bpm: number): number {
  return Math.round(bpm * 100) / 100;
}

function clamp01(x: number): number {
  return Number.isFinite(x) ? Math.max(0, Math.min(1, x)) : 0;
}
